import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { MessageCircle, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { apiRequest } from "@/lib/queryClient";
import { getTrackingParams, trackEvent } from "@/pages/landing";

type ChatItem =
  | { kind: "message"; role: "user" | "assistant"; content: string }
  | { kind: "lead-form"; submitted: boolean };

const WELCOME_TEXT =
  "¡Hola! Soy el asistente de MAPA35. Puedo ayudarte a resolver dudas sobre la NOM-035 y cómo te ayudamos a cumplirla. ¿En qué te apoyo?";

const FALLBACK_ERROR_MESSAGE =
  "No pudimos conectar con el asistente. Contáctanos por WhatsApp mientras lo resolvemos.";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function describeError(error: unknown): string {
  if (error instanceof Error) {
    const match = error.message.match(/^(\d{3}):\s*([\s\S]*)$/);
    if (match) {
      const status = match[1];
      let serverMessage = match[2];
      try {
        const parsed = JSON.parse(match[2]);
        if (parsed?.message) serverMessage = parsed.message;
      } catch {
        // el cuerpo no era JSON, se deja el texto tal cual
      }
      if (status === "429") {
        return serverMessage || "Demasiadas solicitudes seguidas. Intenta de nuevo en unos minutos.";
      }
      return `${FALLBACK_ERROR_MESSAGE} (${status}: ${serverMessage})`;
    }
  }
  return FALLBACK_ERROR_MESSAGE;
}

async function readChatStream(
  res: Response,
  handlers: {
    onDelta: (content: string) => void;
    onLeadCaptured?: () => void;
    onError?: (message: string) => void;
  },
) {
  const reader = res.body?.getReader();
  if (!reader) throw new Error("No se pudo leer la respuesta del asistente.");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? "";

    for (const frame of frames) {
      const line = frame.trim();
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (!payload) continue;

      let event: { type: string; content?: string; message?: string };
      try {
        event = JSON.parse(payload);
      } catch {
        continue;
      }

      if (event.type === "delta" && event.content) {
        handlers.onDelta(event.content);
      } else if (event.type === "lead_captured") {
        handlers.onLeadCaptured?.();
      } else if (event.type === "error") {
        handlers.onError?.(event.message || FALLBACK_ERROR_MESSAGE);
      }
    }
  }
}

export function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<ChatItem[]>([{ kind: "message", role: "assistant", content: WELCOME_TEXT }]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [leadFormShown, setLeadFormShown] = useState(false);
  const [leadNombre, setLeadNombre] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadTelefono, setLeadTelefono] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [hasUnread, setHasUnread] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const isOpenRef = useRef(isOpen);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const exchangeCountRef = useRef(0);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [items]);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    // border-box sizing excludes the border from scrollHeight, so it must be added back
    // or the new height ends up ~border-width short, causing a spurious internal scrollbar.
    const borderWidth = el.offsetHeight - el.clientHeight;
    el.style.height = `${Math.min(el.scrollHeight + borderWidth, 180)}px`;
  }, [input]);

  function toggleOpen() {
    const next = !isOpen;
    setIsOpen(next);
    if (next) {
      setHasUnread(false);
      trackEvent("chatbot_open", { event_category: "chatbot", event_label: "boton_flotante" });
    }
  }

  function messageHistory() {
    return items
      .filter((item): item is Extract<ChatItem, { kind: "message" }> => item.kind === "message")
      .map(({ role, content }) => ({ role, content }));
  }

  function updateLastMessage(content: string) {
    setItems((prev) => {
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      const last = updated[lastIndex];
      if (last?.kind === "message") {
        updated[lastIndex] = { ...last, content };
      }
      return updated;
    });
    if (!isOpenRef.current) {
      setHasUnread(true);
    }
  }

  async function sendMessage() {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;

    const history = [...messageHistory(), { role: "user" as const, content: trimmed }];

    setItems((prev) => [
      ...prev,
      { kind: "message", role: "user", content: trimmed },
      { kind: "message", role: "assistant", content: "" },
    ]);
    setInput("");
    setIsStreaming(true);

    let assistantText = "";

    try {
      const res = await apiRequest("POST", "/api/chatbot/message", {
        messages: history,
        utm: getTrackingParams(),
      });

      await readChatStream(res, {
        onDelta: (chunk) => {
          assistantText += chunk;
          updateLastMessage(assistantText);
        },
        onError: (message) => {
          assistantText = message;
          updateLastMessage(assistantText);
        },
      });

      exchangeCountRef.current += 1;
      if (exchangeCountRef.current === 2 && !leadFormShown) {
        setLeadFormShown(true);
        setItems((prev) => [...prev, { kind: "lead-form", submitted: false }]);
      }
    } catch (error) {
      updateLastMessage(describeError(error));
    } finally {
      setIsStreaming(false);
    }
  }

  async function submitLeadForm() {
    const nombre = leadNombre.trim();
    const email = leadEmail.trim();
    const telefono = leadTelefono.trim();
    const emailValid = email.length > 0 && EMAIL_REGEX.test(email);
    const phoneValid = telefono.length > 0 && /^\d{10}$/.test(telefono);
    if (!nombre || (!emailValid && !phoneValid) || isStreaming) return;

    setFormError(null);
    setIsStreaming(true);

    try {
      const payload: Record<string, unknown> = { nombre, utm: getTrackingParams() };
      if (emailValid) payload.email = email;
      if (phoneValid) payload.telefono = telefono;

      const res = await apiRequest("POST", "/api/chatbot/lead", payload);

      setItems((prev) => [
        ...prev.map((item) => (item.kind === "lead-form" ? { ...item, submitted: true } : item)),
        { kind: "message", role: "assistant", content: "" },
      ]);

      let confirmationText = "";
      await readChatStream(res, {
        onDelta: (chunk) => {
          confirmationText += chunk;
          updateLastMessage(confirmationText);
        },
        onLeadCaptured: () => {
          trackEvent("chatbot_lead_captured", { event_category: "chatbot", event_label: "formulario" });
        },
      });
    } catch (error) {
      setFormError(describeError(error));
    } finally {
      setIsStreaming(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !isStreaming) {
      e.preventDefault();
      sendMessage();
    }
  }

  const leadNombreTrimmed = leadNombre.trim();
  const leadEmailTrimmed = leadEmail.trim();
  const leadTelefonoTrimmed = leadTelefono.trim();
  const leadEmailValid = leadEmailTrimmed.length === 0 || EMAIL_REGEX.test(leadEmailTrimmed);
  const leadTelefonoValid = leadTelefonoTrimmed.length === 0 || /^\d{10}$/.test(leadTelefonoTrimmed);
  const isLeadFormValid =
    leadNombreTrimmed.length > 0 &&
    (leadEmailTrimmed.length > 0 || leadTelefonoTrimmed.length > 0) &&
    leadEmailValid &&
    leadTelefonoValid;

  return (
    <>
      <button
        type="button"
        onClick={toggleOpen}
        aria-label={isOpen ? "Cerrar chat" : "Abrir chat con el asistente de MAPA35"}
        className="fixed bottom-24 right-6 z-50 w-14 h-14 bg-primary hover:scale-110 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 text-primary-foreground"
      >
        {hasUnread && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
            <span className="relative inline-flex h-4 w-4 rounded-full bg-orange-500 border-2 border-white" />
          </span>
        )}
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {isOpen && (
        <Card className="fixed bottom-[168px] right-6 z-40 w-[350px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[70vh] flex flex-col shadow-2xl overflow-hidden">
          <div className="px-4 py-3 border-b bg-primary text-primary-foreground">
            <p className="font-semibold text-sm">Asistente MAPA35</p>
            <p className="text-xs opacity-90">Resolvemos tus dudas sobre NOM-035</p>
          </div>

          <ScrollArea className="flex-1 px-4 py-3">
            <div className="flex flex-col gap-3">
              {items.map((item, index) => {
                if (item.kind === "lead-form") {
                  return (
                    <div key={index} className="rounded-lg border p-3 flex flex-col gap-2 bg-card self-stretch">
                      {item.submitted ? (
                        <p className="text-sm text-muted-foreground">✓ Información enviada</p>
                      ) : (
                        <>
                          <p className="text-sm font-medium">Déjanos tu nombre y correo y/o teléfono para contactarte:</p>
                          <Input
                            type="text"
                            placeholder="Tu nombre"
                            value={leadNombre}
                            onChange={(e) => setLeadNombre(e.target.value)}
                            disabled={isStreaming}
                            aria-label="Nombre"
                          />
                          <Input
                            type="email"
                            placeholder="Tu correo"
                            value={leadEmail}
                            onChange={(e) => setLeadEmail(e.target.value)}
                            disabled={isStreaming}
                            aria-label="Correo electrónico"
                          />
                          {leadEmailTrimmed.length > 0 && !leadEmailValid && (
                            <p className="text-xs text-destructive">Correo con formato inválido.</p>
                          )}
                          <Input
                            type="tel"
                            inputMode="numeric"
                            placeholder="Tu teléfono a 10 dígitos"
                            value={leadTelefono}
                            onChange={(e) => setLeadTelefono(e.target.value.replace(/\D/g, "").slice(0, 10))}
                            disabled={isStreaming}
                            aria-label="Teléfono"
                          />
                          {leadTelefonoTrimmed.length > 0 && !leadTelefonoValid && (
                            <p className="text-xs text-destructive">El teléfono debe tener 10 dígitos.</p>
                          )}
                          {formError && <p className="text-xs text-destructive">{formError}</p>}
                          <Button
                            type="button"
                            size="sm"
                            onClick={submitLeadForm}
                            disabled={!isLeadFormValid || isStreaming}
                          >
                            Enviar
                          </Button>
                        </>
                      )}
                    </div>
                  );
                }

                return (
                  <div
                    key={index}
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                      item.role === "user"
                        ? "self-end bg-primary text-primary-foreground"
                        : "self-start bg-muted text-foreground"
                    }`}
                  >
                    {item.content || (isStreaming && index === items.length - 1 ? "..." : "")}
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
          </ScrollArea>

          <div className="p-3 border-t flex gap-2 items-end">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu pregunta..."
              disabled={isStreaming}
              aria-label="Mensaje para el asistente"
              rows={1}
              className="min-h-0 max-h-[180px] resize-none py-2 leading-snug overflow-y-auto"
            />
            <Button
              type="button"
              size="icon"
              onClick={sendMessage}
              disabled={isStreaming || !input.trim()}
              aria-label="Enviar mensaje"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      )}
    </>
  );
}
